<template>
    <div>
        <v-card class="rounded-lg" :loading="loading || saving">
            <v-tabs v-model="tab">
                <v-tab>General</v-tab>
                <v-tab>Meeting Entry</v-tab>
                <v-tab>Exit Screen</v-tab>
                <v-tab>Branding</v-tab>
                <v-tab>Host Profile</v-tab>
            </v-tabs>

            <v-card-title>
                <v-row>
                    <v-col>
                        <v-btn text @click="$router.push('/admin/rooms')" color="primary" class="mr-2">
                            <v-icon color="primary">mdi-arrow-left</v-icon> Rooms
                        </v-btn> / {{room_id == 'new' ? 'New Room' : 'Edit Room'}}
                    </v-col>
                    <v-col cols="3" class="text-right" v-if="userIsAdmin()">
                        <v-switch 
                            dense hide-details
                            class="mt-1"
                            v-model="room.default_room" 
                            inset 
                            label="Default Room" 
                            color="primary"
                            @change="toggleDefaultRoom()"
                            :disabled="room.default_room"
                            ></v-switch>
                    </v-col>
                    <v-col cols="3" class="text-right" v-if="userIsAdmin()">
                        <v-switch 
                            dense hide-details
                            class="mt-1"
                            v-model="room.settings.preset" 
                            inset 
                            label="Preset" 
                            color="primary"
                            ></v-switch>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <v-tabs-items v-model="tab" class="fill-height">
                <v-tab-item>
                    <v-card-text>

                        <field-label label="Room Name" help_text="Identify your room, for yourself and others." />
                        <v-text-field color="accent" outlined v-model="room.post_title" :rules="[rules.required]"></v-text-field>

                        <!-- ROOM TYPE (MEETING/WEBINAR) -->
                        <template v-if="false && userIsAdmin()">
                            <field-label label="Room Type" help_text="Your room can be primarily for small meetings or live streaming to a group of viewers." />
                            <v-radio-group v-model="room.settings.room_type" row>
                                <v-radio label="Meeting" value="meeting"></v-radio>
                                <v-radio label="Webinar/Livestream" value="livestream"></v-radio>
                            </v-radio-group>
                        </template>
                        
                        <!-- ROOM ID/SLUG (post_name) -->
                        <field-label label="URL/Slug" help_text="This will serve as the direct link to the room." />
                        <v-text-field outlined v-model="room.post_name" messages="Your entry will automatically be adjusted based on availability and allowed characters.">
                            <template v-slot:prepend-inner>
                                <div class="mt-1">
                                    {{windowLocationOrigin}}/
                                </div>
                            </template>
                            <template v-slot:append>
                                <v-btn icon :href="`/${room.post_name}`" target="_blank"><v-icon>mdi-open-in-new</v-icon></v-btn>
                                <v-btn icon @click="copyURL"><v-icon>mdi-content-copy</v-icon></v-btn>
                            </template>
                        </v-text-field>
                        
                        <!-- DESCRIPTION -->
                        <field-label label="Description" help_text="Let guests and others know what this room is used for." />
                        <v-text-field outlined v-model="room.settings.description"></v-text-field>

                        <field-label label="Automatic Recording" help_text="If turned on, all meetings will be recorded automatically." />
                        <v-switch 
                            dense hide-details
                            class="mt-1 mb-8"
                            v-model="room.settings.autorecord" 
                            inset 
                            label="Auto-record" 
                            color="primary"
                        ></v-switch>

                        <field-label label="Virtual Lobby" help_text="If turned on, this room will function as a holding place for guests. The name of everyone in the lobby will appear in all other rooms as in the Lobby and can be brought into the other  rooms by their host." />
                        <p>If turned on, this room will function as a holding place for guests. The name of everyone in this room will appear in all other rooms as in the Lobby and can be brought into the other rooms by their host. People in this room will see a basic screen showing your logo and colors and a "waiting" indicator. People in the room can not see other particpants.</p>
                        <v-switch 
                            dense hide-details
                            class="mt-1"
                            v-model="room.settings.lobby" 
                            inset 
                            label="Use as Virtual Lobby" 
                            color="primary"
                        ></v-switch>

                        <!-- <v-textarea
                            v-if="room.settings.lobby == 1"
                            v-model="room.settings.lobby_users"
                            outlined
                            class="mt-4"
                            label="List the email addresses (one per line) of those that can see this lobby."
                            messages="Email addresses must match those of the other users of this platform that you want to allow to see the guests that are in this lobby."
                        ></v-textarea> -->

                        <template v-if="room.settings.lobby == 1">
                            <v-text-field 
                                color="accent" 
                                outlined 
                                v-model="add_lobby_user"
                                @keyup.enter="addLobbyUser"
                                class="mt-8"
                                label="Enter an email address to authorize the user for this lobby"
                            >
                                <template v-slot:append>
                                    <v-btn icon v-if="add_lobby_user.length > 0" @click="addLobbyUser"><v-icon>mdi-check-circle</v-icon></v-btn>
                                </template>
                            </v-text-field>

                            <div v-if="room.settings.lobby_users && room.settings.lobby_users.length > 0">
                                Authorized Lobby Viewers
                            </div>
                            <div v-for="lobby_user in room.settings.lobby_users" :key="lobby_user">
                                <v-btn icon
                                    @click="removeLobbyUser(lobby_user)"
                                >
                                    <v-icon>mdi-close-circle-outline</v-icon>
                                </v-btn>
                                {{ lobby_user }}
                            </div>
                        </template>

                    </v-card-text>
                    <!-- DELETE -->
                    <v-card-actions class="pa-4">
                        <v-btn color="error" large text @click="show_confirm_delete_room = true">Delete Room</v-btn>
                    </v-card-actions>
                </v-tab-item>
            
                <v-tab-item>
                    <v-card-text>
                        <!-- PASSWORD -->
                        <field-label label="Room Password" help_text="Guests are required to enter the meeting password if one is set here." />
                        <v-text-field outlined v-model="room.settings.password"></v-text-field>

                        <!-- WAITING ROOM -->
                        <field-label label="Waiting Room/Entry" help_text="This settings determines how guests can enter your meeting room." />
                        <v-select 
                            outlined 
                            v-model="room.settings.waiting_room"
                            :items="[
                                { text: 'None - Guests can enter immediately upon arrival', value: 'none'},
                                { text: 'Wait for Host - Guests can enter when a host is present', value: 'wait-for-host'},
                                { text: 'Host Approval - Guests are admitted manually by the host', value: 'host-approval'},
                            ]"
                        ></v-select>

                        <field-label label="Guest Email Field Settings" help_text="You can show the email field for Guest logins and make if required for entry if needed." />
                        <v-switch 
                            dense hide-details
                            class="mt-1 mb-8"
                            v-model="room.settings.show_email_field" 
                            inset 
                            label="Show Email Field" 
                            color="primary"
                        ></v-switch>

                        <v-switch 
                            dense hide-details
                            class="mt-1 mb-8"
                            v-model="room.settings.require_email_field" 
                            inset 
                            label="Require Email Field" 
                            color="primary"
                        ></v-switch>

                    </v-card-text>
                </v-tab-item>
                <v-tab-item>
                    <v-card-text>
                        <!-- Exit Button Text -->
                        <field-label label="Exit Button Text" help_text="After the meeting ends, the final screen will display this text. Default is 'Thanks for Joining'" />
                        <v-text-field outlined v-model="room.settings.exit_button_text"></v-text-field>
                        
                        <!-- Exit Button URL -->
                        <field-label label="Exit Button URL" help_text="After the meeting ends, the final screen will display this a button. You can add a URL here that will link the button to when clicked.'" />
                        <v-text-field outlined v-model="room.settings.exit_button_url"></v-text-field>

                        <!-- EXIT PAGE IMAGE  -->
                        <field-label label="Exit Page Image" help_text="If set, this image is displayed on the exit screen for your room. If one is not supplied, the logo image will be used by default." />
                        <v-card v-if="room.settings.exit_image" outlined class="pa-6">
                            <v-card-text>
                                <v-img v-if="room.settings.exit_image" :src="room.settings.exit_image" max-height="200" contain style=""></v-img>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn outlined color="error" @click="room.settings.exit_image = null">Remove Image</v-btn>
                                <v-btn outlined color="primary" @click="openFiles($refs.exit_image_file)">Replace Image</v-btn>
                                <v-spacer></v-spacer>
                            </v-card-actions>
                        </v-card>
                        <v-card v-else outlined>
                            <v-card-text class="text-center">
                                <v-btn text color="primary" @click="openFiles($refs.exit_image_file)">Upload Exit Image (png, jpg)</v-btn>
                            </v-card-text>
                        </v-card>
                        <div style="display:none;">
                            <input type="file" name="exit_image" ref="exit_image_file" hidden @change="handleSelectedFiles" :accept="accepted_file_types.join(',')">
                            <button onclick="open_file()"></button>
                        </div>  

                    </v-card-text>
                </v-tab-item>

                <v-tab-item>

                    <template v-if="user.hasCapability('manage_brand')">
                        <v-container>
                            <div class="text-h5 mt-8">Theme/Colors</div>
                            <div>These are the settings for the meeting room colors and logo. You can use a predefined template or use your own settings.</div>
                        </v-container>
                        <v-card class="mt-4 rounded-lg">
                            <v-card-text>

                                <!-- BEGIN ADMIN THEME SETTINGS -->
                                <!-- <template v-if="userIsAdmin()"> -->
                                
                                    <field-label label="Room Templates/Presets" help_text="Predefined room settings to speed up your set up." />
                                    <v-select 
                                        outlined
                                        v-model="preset"
                                        :items="presets"
                                        item-text="post_title"
                                        item-value="ID"
                                        @change="loadPreset"></v-select>
                                    
                                    <v-switch
                                        v-model="show_detailed_settings"
                                        label="Show detailed theme settings"
                                    ></v-switch>

                                    <template v-if="show_detailed_settings">
                                        <!-- LOGO  -->
                                        <field-label label="Header Logo" help_text="The logo image you want to display in your meeting room." />
                                        <v-card v-if="room.settings.header_logo" outlined class="pa-6">
                                            <v-card-text>
                                                <v-img :src="room.settings.header_logo" max-height="75" contain></v-img>
                                            </v-card-text>
                                            <v-card-actions>
                                                <v-spacer></v-spacer>
                                                <v-btn outlined color="error" @click="room.settings.header_logo = null">Remove Image</v-btn>
                                                <v-btn outlined color="primary" @click="openFiles($refs.logo_file)">Replace Image</v-btn>
                                                <v-spacer></v-spacer>
                                            </v-card-actions>
                                        </v-card>
                                        <v-card v-else outlined>
                                            <v-card-text class="text-center">
                                                <v-btn text color="primary" @click="openFiles($refs.logo_file)">Upload Logo (png, jpg)</v-btn>
                                            </v-card-text>
                                        </v-card>
                                        <div style="display:none;">
                                            <input type="file" name="header_logo" ref="logo_file" hidden @change="handleSelectedFiles" :accept="accepted_file_types.join(',')">
                                            <button onclick="open_file()"></button>
                                        </div>


                                        <!-- DARK THEME -->
                                        <field-label class="mt-8" label="Dark Theme" help_text="Many users prefer a dark theme, however, you have the option to use a light theme if it works better with your color selection or other preference." />
                                        <v-switch v-model="room.settings.dark" label="Use dark theme"></v-switch>
                                        

                                        <!-- HEADER COLOR -->
                                        <field-label label="Header Color" help_text="This color is used in the top banner of the website, providing the background to your logo image." />
                                        <v-text-field outlined v-model="room.settings.header_color.hex" messages="Hex color code">
                                            <template v-slot:append>
                                                <v-btn fab x-small @click="show_header_color_picker = true" :color="room.settings.header_color.hex"><v-icon>mdi-eyedropper-variant</v-icon></v-btn>
                                            </template>
                                        </v-text-field>

                                        
                                        <!-- BACKGROUND COLOR -->
                                        <field-label label="Background Color" help_text="This is the background of the video area." />
                                        <v-text-field outlined v-model="room.settings.background_color.hex" messages="Hex color code">
                                            <template v-slot:append>
                                                <v-btn fab x-small @click="show_background_color_picker = true" :color="room.settings.background_color.hex"><v-icon>mdi-eyedropper-variant</v-icon></v-btn>
                                            </template>
                                        </v-text-field>

                                        
                                        <!-- PRIMARY COLOR -->
                                        <field-label label="Primary Color" help_text="This color is used throughout the site for buttons and other elements and often corresponds to either the header color or logo/brand color." />
                                        <v-text-field outlined v-model="room.settings.primary_color.hex" messages="Hex color code">
                                            <template v-slot:append>
                                                <v-btn fab x-small @click="show_primary_color_picker = true" :color="room.settings.primary_color.hex"><v-icon>mdi-eyedropper-variant</v-icon></v-btn>
                                            </template>
                                        </v-text-field>


                                        <!-- SECONDARY COLOR -->
                                        <field-label label="Secondary Color" help_text="Used on some functional buttons." />
                                        <v-text-field outlined v-model="room.settings.secondary_color.hex" messages="Hex color code">
                                            <template v-slot:append>
                                                <v-btn fab x-small @click="show_secondary_color_picker = true" :color="room.settings.secondary_color.hex"><v-icon>mdi-eyedropper-variant</v-icon></v-btn>
                                            </template>
                                        </v-text-field>
                                        

                                        <!-- ACCENT COLOR -->
                                        <field-label label="Accent Color" help_text="This color is used throughout the site for items like form fields in the chat." />
                                        <v-text-field outlined v-model="room.settings.accent_color.hex" messages="Hex color code">
                                            <template v-slot:append>
                                                <v-btn fab x-small @click="show_accent_color_picker = true" :color="room.settings.accent_color.hex"><v-icon>mdi-eyedropper-variant</v-icon></v-btn>
                                            </template>
                                        </v-text-field>

                                    </template>
                                <!-- END ADMIN THEME SETTINGS -->
                                

                                <!-- EXIT URL -->
                                <!-- <div class="text-h5 mt-8">Exit URL </div>
                                <p>When the meeting ends or when a user leaves a meeting, you can specify a URL you want them to be directed to.</p>
                                <v-text-field outlined label="Exit URL" v-model="room.settings.exit_url"></v-text-field> -->
                                
                                
                                <!-- BACKGROUND IMAGE -->
                                <!-- <div class="text-h5 mt-8">Background Image </div>
                                <p>When the meeting ends or when a user leaves a meeting, you can specify a URL you want them to be directed to.</p>
                                <v-text-field outlined label="Exit URL" v-model="room.settings.background_image"></v-text-field> -->


                                <!-- VIDEO URL -->
                                <!-- <div class="text-h5 mt-8">Video URL </div>
                                <p>When the meeting ends or when a user leaves a meeting, you can specify a URL you want them to be directed to.</p>
                                <v-text-field outlined label="Exit URL" v-model="room.settings.video_url"></v-text-field> -->
                                

                                
                            </v-card-text>
                        </v-card>
                    </template>
                </v-tab-item>

                <v-tab-item>
                    <v-container>
                        <div class="text-h5 mt-8">Profile Settings</div>
                        <div>These are the settings for the meeting login screen.</div>
                    </v-container>

                    <v-card class="rounded-lg mt-4">
                        <v-card-text>
                            <!-- PROFILE/HOST IMAGE  -->
                            <field-label label="Host/Room Login Screen Image" help_text="If set, this image is displayed on the login screen for your room." />
                            <v-card v-if="room.settings.profile_image" outlined class="pa-6">
                                <v-card-text>
                                    <v-img v-if="room.settings.profile_image" :src="room.settings.profile_image" max-height="200" contain style=""></v-img>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn outlined color="error" @click="room.settings.profile_image = null">Remove Image</v-btn>
                                    <v-btn outlined color="primary" @click="openFiles($refs.profile_image_file)">Replace Image</v-btn>
                                    <v-spacer></v-spacer>
                                </v-card-actions>
                            </v-card>
                            <v-card v-else outlined>
                                <v-card-text class="text-center">
                                    <v-btn text color="primary" @click="openFiles($refs.profile_image_file)">Upload Profile Image (png, jpg)</v-btn>
                                </v-card-text>
                            </v-card>
                            <div style="display:none;">
                                <input type="file" name="profile_image" ref="profile_image_file" hidden @change="handleSelectedFiles" :accept="accepted_file_types.join(',')">
                                <button onclick="open_file()"></button>
                            </div>

                            <!-- HOST PROFILE NAME -->
                            <field-label class="mt-4" label="Host Profile Name" help_text="Displayed on the login screen" />
                            <v-text-field outlined v-model="room.settings.profile_name"></v-text-field>
                            
                            <!-- HOST PROFILE JOB TITLE -->
                            <field-label label="Host Profile Job Title" help_text="Displayed on the login screen" />
                            <v-text-field outlined v-model="room.settings.profile_job_title"></v-text-field>
                            
                            <!-- HOST PROFILE DESCRIPTION -->
                            <field-label label="Host Profile Description" help_text="Displayed on the login screen" />
                            <v-textarea auto-grow rows="1" outlined v-model="room.settings.profile_description"></v-textarea>
                            
                            <!-- PROFILE PHONE NUMBER -->
                            <field-label label="Host Profile Phone Number" help_text="Displayed on the login screen" />
                            <v-text-field outlined v-model="room.settings.profile_phone_number"></v-text-field>
                            
                            <!-- PROFILE EMAIL ADDRESS -->
                            <field-label label="Host Profile Email Address" help_text="Displayed on the login screen" />
                            <v-text-field outlined label="Profile email address" v-model="room.settings.profile_email_address"></v-text-field>

                            
                        </v-card-text>
                    </v-card>
                </v-tab-item>
            </v-tabs-items>

            <v-card class="mt-4 mb-16 rounded-lg">
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn fab color="primary" large class="px-8" :loading="saving" @click="saveRoom">
                        <v-icon>
                            mdi-check
                        </v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-card>
        

            <v-dialog v-model="show_header_color_picker" width="300" hide-overlay>
                <v-color-picker 
                    v-model="room.settings.header_color"
                    :value="room.settings.header_color.hexa"
                    @update:color="previewColorChange"
                    show-swatches
                    hide-mode-switch
                    mode="hexa"
                    :swatches="swatches"
                ></v-color-picker>
            </v-dialog>

            <v-dialog v-model="show_primary_color_picker" width="300" hide-overlay>
                <v-color-picker 
                    v-model="room.settings.primary_color"
                    :value="room.settings.primary_color.hex"
                    @update:color="previewColorChange"
                    show-swatches
                    hide-mode-switch
                    mode="hexa"
                    :swatches="swatches"
                ></v-color-picker>
            </v-dialog>
            
            <v-dialog v-model="show_background_color_picker" width="300" hide-overlay>
                <v-color-picker 
                    v-model="room.settings.background_color"
                    :value="room.settings.background_color.hex"
                    @update:color="previewColorChange"
                    show-swatches
                    hide-mode-switch
                    mode="hexa"
                    :swatches="swatches"
                ></v-color-picker>
            </v-dialog>
            <v-dialog v-model="show_secondary_color_picker" width="300" hide-overlay>
                <v-color-picker 
                    v-model="room.settings.secondary_color"
                    :value="room.settings.secondary_color.hex"
                    @update:color="previewColorChange"
                    show-swatches
                    hide-mode-switch
                    mode="hexa"
                    :swatches="swatches"
                ></v-color-picker>
            </v-dialog>
            <v-dialog v-model="show_accent_color_picker" width="300" hide-overlay>
                <v-color-picker 
                    v-model="room.settings.accent_color"
                    :value="room.settings.accent_color.hex"
                    @update:color="previewColorChange"
                    show-swatches
                    hide-mode-switch
                    mode="hexa"
                    :swatches="swatches"
                ></v-color-picker>
            </v-dialog>
            <v-dialog v-model="show_confirm_delete_room" max-width="600" overlay-opacity="0.9">
                <v-card>
                    <v-card-title>Delete Room?</v-card-title>
                    <v-card-text>Are you sure you want to delete the room: {{room.post_title}}?</v-card-text>
                    <v-card-actions>
                        <v-btn @click="show_confirm_delete_room = false">Cancel</v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="error" @click="deleteRoom()" :loading="deleting_room">Delete</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        
    </div>
</template>

<script>
import { useUserStore } from '../../stores/user'
import colors from 'vuetify/lib/util/colors'
import axios from 'axios'
// import HelpTip from './HelpTip.vue'
import FieldLabel from './FieldLabel.vue'

export default {
    name: 'Room',
    created: function() {
        this.user = useUserStore()
    },
    components: { FieldLabel },
    data: function() {
        return {
            app: null,
            user: null,
            room: {
                settings: {
                    header_color: {"alpha":1,"hex":"#2196F3","hexa":"#2196F3FF","hsla":{"h":206.57142857142858,"s":0.8974358974358974,"l":0.5411764705882353,"a":1},"hsva":{"h":206.57142857142858,"s":0.8641975308641976,"v":0.9529411764705882,"a":1},"hue":206.57142857142858,"rgba":{"r":33,"g":150,"b":243,"a":1} },
                    background_color: {"alpha":1,"hex":"#191717","hexa":"#191717FF","hsla":{"h":0,"s":0.02476515798462849,"l":0.09433055555555557,"a":1},"hsva":{"h":0,"s":0.04833333333333333,"v":0.09666666666666668,"a":1},"hue":0,"rgba":{"r":25,"g":23,"b":23,"a":1}},
                    primary_color: {"alpha":1,"hex":"#2196F3","hexa":"#2196F3FF","hsla":{"h":206.57142857142858,"s":0.8974358974358974,"l":0.5411764705882353,"a":1},"hsva":{"h":206.57142857142858,"s":0.8641975308641976,"v":0.9529411764705882,"a":1},"hue":206.57142857142858,"rgba":{"r":33,"g":150,"b":243,"a":1} },
                    secondary_color: {"alpha":1,"hex":"#424242","hexa":"#424242FF","hsla":{"h":0,"s":0,"l":0.25882352941176473,"a":1},"hsva":{"h":0,"s":0,"v":0.25882352941176473,"a":1},"hue":0,"rgba":{"r":66,"g":66,"b":66,"a":1}},
                    accent_color: {"alpha":1,"hex":"#82B1FF","hexa":"#82B1FFFF","hsla":{"h":217.44,"s":1,"l":0.7549019607843137,"a":1},"hsva":{"h":217.44,"s":0.4901960784313726,"v":1,"a":1},"hue":217.44,"rgba":{"r":130,"g":177,"b":255,"a":1}},
                    dark: true,
                    default: false,
                    header_logo: '',
                    waiting_room: 'host-approval',
                },
                default_room: false
            },
            loading: false,
            saving: false,
            room_id: null,
            show_primary_color_picker: false,
            show_header_color_picker: false,
            show_background_color_picker: false,
            show_secondary_color_picker: false,
            show_accent_color_picker: false,
            rules: {
                required: value => !!value || 'Required.',
                counter: value => value.length <= 20 || 'Max 20 characters',
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                },
            },
            accepted_file_types: ['image/jpeg', 'image/png', 'image/gif'],
            files: [],
            uploading_file: false,
            uploaded_file: null,
            show_file_upload: false,
            show_confirm_delete_room: false,
            deleting_room: false,
            swatches: [],
            presets: [],
            preset: null,
            show_detailed_settings: false,
            // default_room: null,
            tab: null,
            add_lobby_user: "",
        }
    },
    mounted: function() {
        this.room_id = this.$route.params.id
        this.room.default_room = false
        this.loadRoom()
        this.loadPresets()
        // this.default_room = this.user.app.site.page_on_front == this.$route.params.id
    },
    computed: {
        windowLocationOrigin: function() {
            return window.location.origin
        }
    },
    methods: {
        addLobbyUser: function() {
            console.log(typeof this.room.settings.lobby_users)
            if(typeof this.room.settings.lobby_users == 'object')
                this.room.settings.lobby_users.push(this.add_lobby_user)
            else
                this.room.settings.lobby_users = [this.add_lobby_user]
            this.add_lobby_user = ""
        },
        removeLobbyUser: function(removeUser) {
            this.room.settings.lobby_users = this.room.settings.lobby_users.filter(user => user != removeUser)
        },
        toggleDefaultRoom: function() {
            if(this.room.default_room)
                this.user.app.post('meeting/default_room', { room_id: this.room_id })
                    .then(response => {
                        this.user.app.showSuccess('Default page updated')
                    })
                    .catch(error => {
                        this.user.app.showError('')
                    })
        },
        userIsAdmin: function() {
            return this.user.userdata.roles.includes('administrator') || this.user.userdata.roles.includes('editor')
        },
        async copyURL() {
            await navigator.clipboard.writeText(window.location.origin + '/' + this.room.post_name);
            this.user.app.showSuccess('URL Copied to Clipboard', 3)
        },
        deleteRoom: function() {
            this.deleting_room = true
            this.user.app.delete(`meeting/room/${this.room_id}`).then(response => {
                this.deleting_room = false
                this.$router.push('/admin/rooms')
            })
        },
        openFiles: function(ref) {
            // document.getElementById('input_file').click();
            // this.$refs.logo_file.click()
            ref.click()
        },
        handleSelectedFiles: function(e) {
            console.log(e.target.files)
            console.log(e.target.name)
            // return
            if (e.target.files && e.target.files[0]) {
                this.files.push(...e.target.files)
            }

            this.uploading_files = true
            let formData = new FormData();          
            this.files.forEach((file) => {
                formData.append(file.name, file)
            })

            let url = this.user.app.api.url + "lead/communications/upload_files"

            let headers = {
                'X-WP-Nonce': this.user.app.api.restNonce,
            }

            var g = this
            axios.post(url, formData, {
                headers,
                onUploadProgress: function(progressEvent) {
                    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    g.upload_progress = percentCompleted
                    // console.log(percentCompleted)
                }
            }).then(response => {
                console.log("response2:", response.data[0].post_content)
                console.log("response1:", response)
                g.uploading_file = false
                g.uploaded_file = response.data
                g.files = []
                g.show_file_upload = false
                g.room.settings[e.target.name] = response.data[0].post_content
                if(!g.user.app.room.settings)
                    g.$set(g.user.app.room, 'settings', {[e.target.name]: ''})
                g.$set(g.user.app.room.settings, e.target.name, response.data[0].post_content)
                g.loadColors()
                // g.sendMessage()
            }).catch(error => g.uploading_files = false)
        },
        loadRoom: function() {
            if(this.room_id == 'new') {
                this.loadColors()
                return
            }

            this.loading = true
            this.user.app.get(`meeting/room/${this.room_id}`).then(response => {
                let default_settings = {
                    header_color: { hex: colors.indigo },
                    background_color: { hex: colors.grey.darken3 },
                    primary_color: { hex: colors.indigo.lighten1 },
                    secondary_color: { hex: colors.grey.darken3 },
                    accent_color: { hex: colors.indigo.accent1 },
                    room_type: 'meeting'
                }
                let settings = { ...default_settings, ...response.data.room.settings }
                response.data.room.settings = settings

                this.room = response.data.room
                this.loading = false


                // this.user.app.site.logo_url = this.room.settings.header_logo
                // console.log("set header logo", this.room.settings.header_logo)
                // this.user.app.room.settings.header_logo = this.room.settings.header_logo
                this.user.app.room = this.room
                this.previewColorChange()
                this.loadColors()
            })
        },
        loadColors: function() {
            if(!this.user.hasCapability('manage_brand'))
                return
            this.user.app.post('image_colors', {file: this.room?.settings?.header_logo}).then(response => {
                // response.data.forEach(color => {
                //     this.swatches.push([color])
                // })
                this.swatches = response.data
            })
        },
        loadPresets: function() {
            this.user.app.get('meeting/presets').then(response => {
                this.presets = response.data.presets
                this.presets.forEach(preset => {
                    preset.settings = JSON.parse(preset.post_content)
                })
            })
        },
        saveRoom: function() {
            this.saving = true
            let id = this.room_id == 'new' ? 0 : this.room_id
            // this.room.settings.lobby_users = this.room.settings.lobby_users.split(/\r?\n/)
            this.user.app.post(`meeting/room/${id}`, this.room).then(response => {
                this.saving = false
                // this.$set(this.user.app.room, 'post_name', response.data.room.post_name)
                this.$set(this.user.app, 'room', response.data.room)
                this.user.app.showSuccess('Room Saved')
                this.$router.push({path: `/admin/rooms/${response.data.room.ID}`})
                this.room_id = response.data.room.ID
                this.room.post_name = response.data.room.post_name
            })
            .catch(error => {
                this.user.app.showError('Room could not be saved.')
            })
        },
        previewColorChange: function() {
            // console.log('previewColorChange')
            if(!this.user.hasCapability('manage_brand'))
                return

            this.toggleDarkMode()

            // header
            this.$vuetify.theme.themes.dark.header = this.room.settings.header_color.hex
            this.$vuetify.theme.themes.light.header = this.room.settings.header_color.hex
            
            // background
            this.$vuetify.theme.themes.dark.background = this.room.settings.background_color.hex
            this.$vuetify.theme.themes.light.background = this.room.settings.background_color.hex
            
            // primary
            this.$vuetify.theme.themes.dark.primary = this.room.settings.primary_color.hex
            this.$vuetify.theme.themes.light.primary = this.room.settings.primary_color.hex

            // secondary
            this.$vuetify.theme.themes.dark.secondary = this.room.settings.secondary_color.hex
            this.$vuetify.theme.themes.light.secondary = this.room.settings.secondary_color.hex
            
            // accent
            this.$vuetify.theme.themes.dark.accent = this.room.settings.accent_color.hex
            this.$vuetify.theme.themes.light.accent = this.room.settings.accent_color.hex

        },
        toggleDarkMode: function () {
            this.$vuetify.theme.dark = this.room.settings.dark;
        },
        loadPreset: function() {
            let preset = this.presets.find(preset => {
                return preset.ID == this.preset
            })
            if(!preset)
                return
            this.room.settings.header_color = preset.settings.header_color
            this.room.settings.background_color = preset.settings.background_color
            this.room.settings.primary_color = preset.settings.primary_color
            this.room.settings.secondary_color = preset.settings.secondary_color
            this.room.settings.accent_color = preset.settings.accent_color
            this.room.settings.header_logo = preset.settings.header_logo
            this.user.app.room.settings.header_logo = preset.settings.header_logo
            this.room.settings.dark = preset.settings.dark

            this.previewColorChange()
        }
    },
    watch: {
        'room.settings.dark': function() {
            this.toggleDarkMode()
        },
        // 'room_id': function(newV, oldV) {
        //     console.log(newV, oldV, this.room.default_room)
        //     this.room.default_room = false
        // }
    }
}
</script>